<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Page Header integrated into Form Card -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Add New Consultation</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Create a new consultation record for patient care management
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Back Button and Form Badge -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-stethoscope</v-icon>
                      New Consultation Form
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Consultations
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                
                <!-- Basic Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-information</v-icon>
                    Basic Information
                  </h3>
                  
                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                        v-model="form.title"
                        label="Consultation Title"
                        placeholder="Enter consultation title..."
                        outlined
                        :rules="[(v) => !!v || 'Consultation title is required']"
                        :error-messages="errors.title"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-format-title</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.doctor_id"
                        :items="doctors"
                        item-text="text"
                        item-value="value"
                        label="Select Doctor"
                        placeholder="Choose a doctor..."
                        outlined
                        :rules="[(v) => !!v || 'Doctor selection is required']"
                        :error-messages="errors.doctor_id"
                        class="professional-field"
                        hide-details="auto"
                        clearable
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-doctor</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.patient_id"
                        :items="patients"
                        item-text="text"
                        item-value="value"
                        label="Select Patient"
                        placeholder="Choose a patient..."
                        outlined
                        :rules="[(v) => !!v || 'Patient selection is required']"
                        :error-messages="errors.patient_id"
                        class="professional-field"
                        hide-details="auto"
                        clearable
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account-heart</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                </div>

                <!-- Consultation Details Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-medical-bag</v-icon>
                    Consultation Details
                  </h3>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.type_id"
                        :items="consultationTypes"
                        item-text="text"
                        item-value="value"
                        label="Consultation Type"
                        placeholder="Select consultation type..."
                        outlined
                        :rules="[(v) => !!v || 'Consultation type is required']"
                        :error-messages="errors.type_id"
                        class="professional-field"
                        hide-details="auto"
                        clearable
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-medical-bag</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.appointment_id"
                        label="Appointment ID (Optional)"
                        placeholder="Enter appointment ID if applicable..."
                        outlined
                        type="number"
                        :error-messages="errors.appointment_id"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-calendar</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Schedule Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-clock</v-icon>
                    Schedule Information
                  </h3>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.start_time"
                        label="Start Time"
                        placeholder="Select consultation start time..."
                        outlined
                        type="datetime-local"
                        :rules="[(v) => !!v || 'Start time is required']"
                        :error-messages="errors.start_time"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-clock-start</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.end_time"
                        label="End Time"
                        placeholder="Select consultation end time..."
                        outlined
                        type="datetime-local"
                        :error-messages="errors.end_time"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-clock-end</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Status and Notes Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-cog</v-icon>
                    Status & Notes
                  </h3>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.status"
                        :items="statusOptions"
                        item-text="text"
                        item-value="value"
                        label="Consultation Status"
                        placeholder="Select consultation status..."
                        outlined
                        :rules="[(v) => v !== null && v !== undefined || 'Status is required']"
                        :error-messages="errors.status"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-check-circle</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                        v-model="form.notes"
                        label="Consultation Notes"
                        placeholder="Enter detailed consultation notes..."
                        outlined
                        rows="4"
                        :error-messages="errors.notes"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-note-text</v-icon>
                        </template>
                      </v-textarea>
                    </v-col>
                  </v-row>
                </div>

              </v-form>
            </v-card-text>

            <!-- Form Actions Footer -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions pa-2">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip 
                      small 
                      :color="hasValidationErrors() ? 'error' : 'success'"
                      :text-color="hasValidationErrors() ? 'white' : 'white'"
                      class="mr-3"
                    >
                      <v-icon left x-small>
                        {{ hasValidationErrors() ? 'mdi-alert-circle' : 'mdi-check-circle' }}
                      </v-icon>
                      {{ hasValidationErrors() ? 'Please fix validation errors' : 'Form validation passed' }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      All required fields must be completed
                    </span>
                  </div>
                </v-col>
                
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end">
                    <v-btn
                      color="grey"
                      outlined
                      class="mr-3 action-button"
                      @click="$router.back()"
                      :disabled="isSaving"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="action-button"
                      @click="saveConsultation"
                      :loading="isSaving"
                      :disabled="isSaving || !valid"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Save Consultation
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Success Dialog -->
      <v-dialog v-model="showSuccessDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-check-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Consultation created successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="
                          showSuccessDialog = false;
                          $router.push('/consultations');
                        "
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "ConsultationCreate",
  data() {
    return {
      valid: false,
      isSaving: false,
      showSuccessDialog: false,
      doctors: [],
      patients: [],
      errors: {},

      form: {
        title: "",
        doctor_id: null,
        patient_id: null,
        type_id: null,
        appointment_id: null,
        start_time: "",
        end_time: "",
        status: false,
        is_active: true,
        notes: "",
      },

      consultationTypes: [
        { value: 1, text: "General Consultation" },
        { value: 2, text: "Follow-up" },
        { value: 3, text: "Specialist Consultation" },
        { value: 4, text: "Emergency Consultation" },
        { value: 5, text: "Routine Check-up" },
      ],

      statusOptions: [
        { value: false, text: "Pending" },
        { value: true, text: "Completed" },
      ],
    };
  },

  mounted() {
    this.getMasters();
  },

  methods: {
    hasValidationErrors() {
      return Object.keys(this.errors).length > 0 || !this.valid;
    },

    async getMasters() {
      try {
        const response = await axios.get("masters?context=consultations");

        if (response.data?.users) {
          // Filter doctors
          this.doctors = response.data.users
            .filter((user) =>
              user.position?.name?.toLowerCase().includes("doctor")
            )
            .map((user) => ({
              text: user.user_name,
              value: user.id,
            }));

          // Filter patients
          this.patients = response.data.users
            .filter((user) =>
              user.position?.name?.toLowerCase().includes("patient")
            )
            .map((user) => ({
              text: user.user_name,
              value: user.id,
            }));

          // If no users found with specific positions, show all users as fallback
          if (this.doctors.length === 0) {
            this.doctors = response.data.users.map((user) => ({
              text: user.user_name,
              value: user.id,
            }));
            console.warn(
              "No doctors found with specific positions, showing all users"
            );
          }
          if (this.patients.length === 0) {
            this.patients = response.data.users.map((user) => ({
              text: user.user_name,
              value: user.id,
            }));
            console.warn(
              "No patients found with specific positions, showing all users"
            );
          }
        }
      } catch (error) {
        console.error("Error fetching masters:", error);
      }
    },

    async saveConsultation() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.isSaving = true;
      this.errors = {};

      try {
        // Prepare the data
        const consultationData = {
          title: this.form.title,
          doctor_id: this.form.doctor_id,
          patient_id: this.form.patient_id,
          type_id: this.form.type_id,
          appointment_id: this.form.appointment_id,
          start_time: this.form.start_time,
          end_time: this.form.end_time,
          status: this.form.status,
          is_active: this.form.is_active,
          notes: this.form.notes,
        };

        const response = await axios.post("/consultations", consultationData);

        if (response.data.success) {
          this.showSuccessDialog = true;
          this.resetForm();
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while creating the consultation.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response) {
          if (error.response.status === 422) {
            // Validation errors
            this.errors = error.response.data.errors || {};
          } else if (error.response.status === 401) {
            // Authentication error
            this.$store.dispatch("errors/setError", {
              message: "Authentication required. Please login again.",
            });
            this.$router.push("/login");
          } else {
            // Other errors
            this.$store.dispatch("errors/setError", {
              message:
                error.response.data.message ||
                "An error occurred while creating the consultation.",
            });
          }
        } else {
          // Network or other errors
          this.$store.dispatch("errors/setError", {
            message:
              "Network error. Please check your connection and try again.",
          });
        }
      } finally {
        this.isSaving = false;
      }
    },

    resetForm() {
      this.form = {
        title: "",
        doctor_id: null,
        patient_id: null,
        type_id: null,
        appointment_id: null,
        start_time: "",
        end_time: "",
        status: false,
        is_active: true,
        notes: "",
      };
      this.errors = {};
      this.$refs.form.resetValidation();
    },
  },
  
  watch: {
    // Clear errors when user starts typing
    "form.title"() {
      if (this.errors.title) {
        this.$delete(this.errors, "title");
      }
    },
    "form.doctor_id"() {
      if (this.errors.doctor_id) {
        this.$delete(this.errors, "doctor_id");
      }
    },
    "form.patient_id"() {
      if (this.errors.patient_id) {
        this.$delete(this.errors, "patient_id");
      }
    },
    "form.type_id"() {
      if (this.errors.type_id) {
        this.$delete(this.errors, "type_id");
      }
    },
    "form.appointment_id"() {
      if (this.errors.appointment_id) {
        this.$delete(this.errors, "appointment_id");
      }
    },
    "form.start_time"() {
      if (this.errors.start_time) {
        this.$delete(this.errors, "start_time");
      }
    },
    "form.end_time"() {
      if (this.errors.end_time) {
        this.$delete(this.errors, "end_time");
      }
    },
    "form.status"() {
      if (this.errors.status) {
        this.$delete(this.errors, "status");
      }
    },
    "form.notes"() {
      if (this.errors.notes) {
        this.$delete(this.errors, "notes");
      }
    },
  },
};
</script>

<style scoped>
/* Professional Form Card with Integrated Header */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border-left: 4px solid var(--v-primary-base);
}

.page-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.form-content {
  background: #ffffff;
}

/* Section Styling */
.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Professional Form Fields */
.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

.professional-field >>> .v-textarea textarea {
  font-size: 0.75rem;
  line-height: 1.4;
}

/* Status Card */
.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Spacing and Alignment */
.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

/* Professional Color Scheme */
.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.1rem;
  }
  
  .form-title {
    font-size: 0.9rem;
  }
  
  .section-title {
    font-size: 0.85rem;
  }
  
  .form-content {
    padding: 0.75rem !important;
  }
  
  .back-button,
  .action-button {
    min-width: 80px;
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {
  .v-row > .v-col {
    padding: 0 4px;
  }
  
  .form-content {
    padding: 0.5rem !important;
  }
  
  .section-title {
    font-size: 0.8rem;
  }
  
  .professional-field >>> .v-input__control {
    min-height: 36px;
  }
  
  .professional-field >>> .v-text-field input {
    font-size: 0.7rem;
    padding-top: 6px;
    padding-bottom: 6px;
  }
  
  .back-button,
  .action-button {
    min-width: 70px;
    height: 28px;
    font-size: 0.65rem;
  }
}

/* Clean Focus States */
.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Consistent Margins */
.mb-1 {
  margin-bottom: 0.5rem !important;
}

.mb-2 {
  margin-bottom: 0.75rem !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

/* Professional Transitions */
.v-card,
.v-btn,
.professional-field,
.status-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>